<template>
  <div class="living-options-masterplan">
    <div class="row">
      <div class="col-12 mb-4">
        <div class="living-options" id="living-options">
          <h3>Living Options</h3>
          <button class="btn btn-sm btn-primary mb-4" @click="addLivingOption">Add Living Option</button>

          <div class="row mb-4">
            <div class="col-6">
              <div class="status-option">
                <input type="checkbox" id="active" value="Active" v-model="filterActiveStates">
                <label class="status-option__label" for="active">Active</label>
              </div>

              <div class="status-option">
                <input type="checkbox" id="inactive" value="Inactive" v-model="filterActiveStates">
                <label class="status-option__label" for="inactive">Inactive</label>
              </div>
            </div>
          </div>

          <div class="mb-2">
            <input class="form-control" type="text" v-model="livingOptionsFilterText" placeholder="Filter Living Options">
          </div>

          <v-table
            :columns="[
              { label: 'Label', field: 'label'},
              { label: 'Visually Hidden', field: 'isVisuallyHidden'},
              { 
                label: 'Sold out',
                field: 'isSoldOut',
                options: {
                  sortable: true,
                  return: function (fieldValue, row) {
                    if (fieldValue) {
                      return `<div class='status status--sold'>Manually Set</div>`
                    } else if (row.availablePropertiesCount === 0) {
                      return `<div class='status status--sold'>Available properties: ${row.availablePropertiesCount}</div>`
                    } else {
                      return `<div class='status status--available'>Available properties: ${row.availablePropertiesCount}</div>`
                    }
                  }
                }
              },
              {
                label: 'Associated Releases',
                field: 'subsectionIds',
                sortField: 'subsectionIds.length',
                options: {
                  sortable: true,
                  return: function (fieldValue) {
                    if (fieldValue.length) {
                      return fieldValue.length
                    } else {
                      return '-'
                    }
                  }
                }
              },
            ]"
            :rows="filteredLivingOptions"
            :show-active-toggle="activeUser.access.indexOf('live-toggle') !== -1"
            save-action="SAVE_LIVING_OPTION"
            @edit="editLivingOption($event._id)"
            @delete="deleteLivingOption($event._id)"
          />
        </div>
      </div>

      <div class="col-12 mb-4">
        <div class="form-group">
          <label>Masterplan Background Image</label>
          <input-asset v-model="liveHereBackground"></input-asset>
        </div>
        <div v-if="liveOptionConfigTags" class="form-group">
          <label>Living Option Placement</label>
          <button class="btn btn-sm btn-primary my-2" @click="addMasterplanTag">add tag</button>
          <p>
            <info-box>
              <ul>
                <li>Click on the Add Tag button to create a tag</li>
                <li>Reposition tags by clicking and dragging</li>
                <li>Click on a tag to display the edit tag dialog box</li>
              </ul>
            </info-box>
          </p>
          <draggable-elements 
            :tags="liveOptionConfigTags.value"
            :living-option-ids="livingOptionIds"
            :background-image="liveHereBackground"
          />
        </div>

        <div class="form-group" id="sidebar-heading">
          <label>Sidebar Heading</label>
          <input
            v-model="liveHereHeading['en']"
            id="liveHeading"
            type="text"
            class="form-control"
          >
        </div>

        <div class="form-group" id="sidebar-description">
          <label>Sidebar Description</label>
          <ck-editor v-model="liveHereDescription['en']" id="liveDescription"></ck-editor>
        </div>
        <div class="form-group">
          <label>Living Sidebar Minimap</label>
          <input-asset v-model="liveHereSidebarMinimap"></input-asset>
        </div>
        <button class="btn btn-sm btn-primary" @click="saveLivingOptions">Save</button>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex'
import { FETCH_CONFIG_ITEMS, CONFIG_ITEMS_EDIT } from '@/store/actions.type'
import CkEditor from '@/components/CKEditor'
import DotDropper from '@/components/DotDropper'
import InputAsset from '@/components/InputAsset'
import VTable from '@/components/VTable'
import InfoBox from '@/components/InfoBox'
import DraggableElements from '@/components/DraggableElements'

export default {
  components: {
    CkEditor,
    DotDropper,
    InputAsset,
    VTable,
    InfoBox,
    DraggableElements
  },

  data () {
    return {
      configItems: [],
      liveHereSidebarMinimap: '',
      liveHereDescription: {
        en: ''
      },
      liveHereHeading: {
        en: ''
      },
      liveHereBackground: '',
      livingOptionsConfigItemsKeys: [
        'liveHereSidebarMinimap',
        'liveHereHeading',
        'liveHereDescription',
        'liveHereBackground'
      ],

      activeLivingOptionId: null,
      livingOptionsFilterText: '',
      filterActiveStates: []
    }
  },

  computed: {
    ...mapGetters([
      'livingOptions'
    ]),

    activeUser () {
      return this.$store.getters.user
    },

    livingOptionIds () {
      return this.livingOptions.map(livingOption => livingOption._id)
    },

    liveOptionConfigTags () {
      return this.configItems.find(item => item.key === 'liveHereMasterplanTags')
    },

    filteredLivingOptions () {
      let filtered = this.livingOptions.filter(item => item.label.toLowerCase().includes(this.livingOptionsFilterText.toLowerCase()))

      if (this.filterActiveStates.length) {
        filtered = filtered.filter(item => (this.filterActiveStates.includes('Active') && item.active) || (this.filterActiveStates.includes('Inactive') && !item.active))
      }

      return filtered
    },   
  },

  created () {
    this.$store.dispatch('FETCH_ASSETS')
    this.$store.dispatch('FETCH_LIVING_OPTIONS')
    this.setData()
  },

  methods: {
    async setData () {
      this.configItems = await this.$store.dispatch(FETCH_CONFIG_ITEMS)

      const liveConfigItems = this.configItems.filter(item => this.livingOptionsConfigItemsKeys.includes(item.key))

      // Map the live items to the component data
      liveConfigItems.forEach(item => {
        this[item.key] = item.value
      })
    },

    async addLivingOption () {
      const newLivingOption = await this.$store.dispatch('ADD_NEW_LIVING_OPTION')
      this.$store.dispatch('SAVE_LIVING_OPTION', newLivingOption)
      this.editLivingOption(newLivingOption._id)
    },

    editLivingOption (id) {
      this.$router.push(`masterplan/${id}`)
    },

    deleteLivingOption (id) {
      if (confirm('Are you sure?')) {
        this.$store.dispatch('DELETE_LIVING_OPTION', id)
      }
    },

    saveConfig () {
      this.configItems.forEach(configItem => {
        this.livingOptionsConfigItemsKeys.forEach(key => {
          if (key === configItem.key) {
            configItem.value = this[key]
          }
        })
      })
      this.$store.dispatch(CONFIG_ITEMS_EDIT, this.configItems)
    },

    saveLivingOptions () {
      this.saveConfig()
      this.livingOptions.forEach(livingOption => {
        this.$store.dispatch('SAVE_LIVING_OPTION', livingOption)
      })
    },

    addMasterplanTag () {
      this.$store.dispatch('GET_TAG_SCHEMA').then(res => {
        this.liveOptionConfigTags.value.push(res.data)
      })
    },

    addLivingOptionTag () {
      this.$store.dispatch('GET_TAG_SCHEMA').then(res => {
        this.activeLivingOption.tags.push(res.data)
      })
    },

    backgroundAssetType (asset) {
      if (asset) {
        if (asset.split('.').pop() === 'mp4') {
          return 'video'
        } else {
          return 'image'
        }
      }

      return ''
    },
  },
}
</script>
